// ---------------------------------------------------------------------------
// Visual classes
// ---------------------------------------------------------------------------
export const ticketPanelClass = 'bg-card rounded-2xl shadow-md border border-border';
export const ticketInputClass =
  'control-inline w-full bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none transition-all';
export const ticketPrimaryButtonClass =
  'control-inline flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium transition-colors disabled:bg-muted disabled:text-muted-foreground/60 disabled:cursor-not-allowed';
export const ticketSecondaryButtonClass =
  'control-inline border border-input text-foreground rounded-xl font-medium hover:bg-muted transition-colors disabled:bg-muted/40 disabled:text-muted-foreground/40 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// Ticket types & statuses registry
// ---------------------------------------------------------------------------

/** Human-readable label for every TicketType value. */
export const TICKET_TYPE_LABELS = {
  ISSUE:           'Issue',
  FEATURE_REQUEST: 'Feature request',
  CHANGE_REQUEST:  'Change request',
  LEAD:            'Sales lead',
  PLAN:            'Strategic plan',
  IT_SERVICE:      'IT service request',
};

/** Human-readable label for every TicketStatus value. */
export const TICKET_STATUS_LABELS = {
  // Classic
  OPEN:        'Open',
  IN_PROGRESS: 'In progress',
  REVIEW:      'Review',
  DONE:        'Done',
  REOPENED:    'Reopened',
  CLOSED:      'Closed',
  // Lead
  NEW_LEAD:       'New lead',
  CONTACTED:      'Contacted',
  DEMO_SCHEDULED: 'Demo scheduled',
  PROPOSAL_SENT:  'Proposal sent',
  WON:            'Won',
  LOST:           'Lost',
  // Plan
  DRAFT:     'Draft',
  APPROVED:  'Approved',
  EXECUTING: 'Executing',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  // IT Service
  PENDING_APPROVAL: 'Pending approval',
  FULFILLING:       'Fulfilling',
  FULFILLED:        'Fulfilled',
  REJECTED:         'Rejected',
};

/**
 * Statuses that count as "active" (ticket is open and being worked on) per type.
 * Used for queue / assigned-ticket bucketing.
 */
export const ACTIVE_TICKET_STATUSES = new Set([
  // Classic
  'OPEN', 'IN_PROGRESS', 'REVIEW', 'REOPENED',
  // Lead
  'NEW_LEAD', 'CONTACTED', 'DEMO_SCHEDULED', 'PROPOSAL_SENT',
  // Plan
  'DRAFT', 'APPROVED', 'EXECUTING',
  // IT Service
  'PENDING_APPROVAL', 'FULFILLING',
]);

/**
 * Statuses that count as "resolved / closed" per type.
 * Used for the History & Closure bucket.
 */
export const RESOLVED_TICKET_STATUSES = new Set([
  'DONE', 'CLOSED', 'WON', 'LOST', 'COMPLETED', 'CANCELLED', 'FULFILLED', 'REJECTED',
]);

/**
 * Every status a given ticket type can ever hold, in pipeline order.
 * Drives the status filter once a type filter is active, so the UI can never
 * offer a type/status pair that is impossible by construction.
 */
export const TICKET_STATUSES_BY_TYPE = {
  ISSUE:           ['OPEN', 'IN_PROGRESS', 'REVIEW', 'REOPENED', 'DONE', 'CLOSED'],
  FEATURE_REQUEST: ['OPEN', 'IN_PROGRESS', 'REVIEW', 'REOPENED', 'DONE', 'CLOSED'],
  CHANGE_REQUEST:  ['OPEN', 'IN_PROGRESS', 'REVIEW', 'REOPENED', 'DONE', 'CLOSED'],
  LEAD:            ['NEW_LEAD', 'CONTACTED', 'DEMO_SCHEDULED', 'PROPOSAL_SENT', 'WON', 'LOST'],
  PLAN:            ['DRAFT', 'APPROVED', 'EXECUTING', 'COMPLETED', 'CANCELLED'],
  IT_SERVICE:      ['OPEN', 'PENDING_APPROVAL', 'APPROVED', 'FULFILLING', 'FULFILLED', 'REJECTED', 'CLOSED'],
};

const toStatusOption = (value) => ({
  value,
  label: TICKET_STATUS_LABELS[value] ?? value.replace(/_/g, ' '),
});

/**
 * Groups statuses by section label for the "All statuses" filter dropdown.
 * Each status value appears exactly once across all groups – a repeated value
 * would make the dropdown highlight (and label) the wrong row, because the
 * Select matches options by value.
 * Each entry is { label: string, options: [{ value, label }] }
 */
export const TICKET_STATUS_GROUPS = [
  {
    label: 'Classic',
    options: ['OPEN', 'IN_PROGRESS', 'REVIEW', 'REOPENED', 'DONE', 'CLOSED'].map(toStatusOption),
  },
  {
    label: 'Sales Lead',
    options: ['NEW_LEAD', 'CONTACTED', 'DEMO_SCHEDULED', 'PROPOSAL_SENT', 'WON', 'LOST'].map(toStatusOption),
  },
  {
    label: 'Strategic Plan',
    options: ['DRAFT', 'APPROVED', 'EXECUTING', 'COMPLETED', 'CANCELLED'].map(toStatusOption),
  },
  {
    label: 'IT Service',
    options: ['PENDING_APPROVAL', 'FULFILLING', 'FULFILLED', 'REJECTED'].map(toStatusOption),
  },
];

/**
 * Canonical display order for statuses that have to share one view – the board
 * lanes when no type filter is active and tickets of several types are mixed.
 * Derived from TICKET_STATUS_GROUPS so the lanes and the filter dropdown can
 * never drift apart, and so every status appears exactly once.
 */
export const TICKET_STATUS_ORDER = TICKET_STATUS_GROUPS.flatMap((group) =>
  group.options.map((option) => option.value)
);

/**
 * Status filter options for the currently selected type filter.
 * Returns the grouped list when no type is selected, and a single flat group
 * scoped to that type otherwise.
 * @param {string} type – '' for "all types"
 * @returns {{ label: string, options: { value: string, label: string }[] }[]}
 */
export const getStatusGroupsForTypeFilter = (type) => {
  if (!type || !TICKET_STATUSES_BY_TYPE[type]) return TICKET_STATUS_GROUPS;
  return [
    {
      label: TICKET_TYPE_LABELS[type] ?? type,
      options: TICKET_STATUSES_BY_TYPE[type].map(toStatusOption),
    },
  ];
};

/**
 * Returns the ordered workflow steps for a given ticket type.
 * Used to render a pipeline progress-bar in TicketDetails.
 * @param {string} type
 * @returns {{ key: string; label: string }[]}
 */
export const getWorkflowStepsForType = (type) => {
  switch (type) {
    case 'LEAD':
      return [
        { key: 'NEW_LEAD',       label: 'New lead' },
        { key: 'CONTACTED',      label: 'Contacted' },
        { key: 'DEMO_SCHEDULED', label: 'Demo' },
        { key: 'PROPOSAL_SENT',  label: 'Proposal' },
        { key: 'WON',            label: 'Won' },
      ];
    case 'PLAN':
      return [
        { key: 'DRAFT',     label: 'Draft' },
        { key: 'APPROVED',  label: 'Approved' },
        { key: 'EXECUTING', label: 'Executing' },
        { key: 'COMPLETED', label: 'Completed' },
      ];
    case 'IT_SERVICE':
      return [
        { key: 'OPEN',             label: 'Submitted' },
        { key: 'PENDING_APPROVAL', label: 'Pending' },
        { key: 'APPROVED',         label: 'Approved' },
        { key: 'FULFILLING',       label: 'Fulfilling' },
        { key: 'FULFILLED',        label: 'Fulfilled' },
      ];
    default:
      return [
        { key: 'OPEN',        label: 'Open' },
        { key: 'IN_PROGRESS', label: 'In progress' },
        { key: 'REVIEW',      label: 'Review' },
        { key: 'DONE',        label: 'Done' },
      ];
  }
};

/**
 * Returns the allowed next statuses for the status picker inside TicketDetails,
 * always including the current status so the picker can show "no change".
 *
 * `workflows` is the effective transition map served by GET /tickets/workflows
 * (DB rows merged over the server defaults). The server is the single source of
 * truth – while it is still loading we offer only the current status rather than
 * guessing from a stale local copy.
 *
 * @param {Record<string, Record<string, string[]>>} workflows
 * @param {string} type
 * @param {string} currentStatus
 * @returns {{ value: string; label: string }[]}
 */
export const getAllowedStatusOptions = (workflows, type, currentStatus) => {
  const allowedNext = workflows?.[type]?.[currentStatus] ?? [];
  return [currentStatus, ...allowedNext]
    .filter((value, index, all) => all.indexOf(value) === index)
    .map(toStatusOption);
};

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

/** Ordered high→low so the filter dropdown reads like a severity scale. */
export const TICKET_PRIORITY_OPTIONS = [
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH',   label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW',    label: 'Low' },
];

export const TICKET_PRIORITY_LABELS = {
  URGENT: 'Urgent',
  HIGH:   'High',
  MEDIUM: 'Medium',
  LOW:    'Low',
};

export const getTicketPriorityClassName = (priority) => {
  switch (priority) {
    case 'URGENT': return getToneClassName('danger');
    case 'HIGH':   return getToneClassName('warning');
    case 'LOW':    return getToneClassName('neutral-strong');
    default:       return getToneClassName('info'); // MEDIUM
  }
};

// ---------------------------------------------------------------------------
// Reference URL
// ---------------------------------------------------------------------------

/**
 * Returns the reference URL only when it is safe to render as a link.
 * The server already rejects non-http(s) URLs on write, but older rows (and any
 * future import path) could still carry a `javascript:` URL – rendering that in
 * an href is an XSS vector, so the check is repeated here at the render edge.
 *
 * @param {string | null | undefined} url
 * @returns {string | null}
 */
export const getSafeReferenceUrl = (url) => {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

/** Short, readable stand-in for a long URL – the host plus a truncated path. */
export const formatReferenceUrl = (url) => {
  try {
    const { hostname, pathname } = new URL(url);
    const host = hostname.replace(/^www\./, '');
    if (!pathname || pathname === '/') return host;
    return `${host}${pathname.length > 24 ? `${pathname.slice(0, 24)}…` : pathname}`;
  } catch {
    return url;
  }
};

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export const getToneClassName = (tone = 'default') => {
  switch (tone) {
    case 'primary':        return 'tone-primary-soft';
    case 'success':        return 'tone-success-soft';
    case 'warning':        return 'tone-warning-soft';
    case 'info':           return 'tone-info-soft';
    case 'danger':         return 'tone-danger-soft';
    case 'neutral-strong': return 'tone-neutral-strong';
    default:               return 'bg-muted text-muted-foreground border-border';
  }
};

export const getTicketStatusClassName = (status) => {
  switch (status) {
    // Classic
    case 'DONE':        return getToneClassName('success');
    case 'IN_PROGRESS': return getToneClassName('info');
    case 'REVIEW':      return getToneClassName('warning');
    case 'REOPENED':    return getToneClassName('danger');
    case 'CLOSED':      return getToneClassName('neutral-strong');
    // Lead – progressive green
    case 'WON':            return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20';
    case 'LOST':           return getToneClassName('neutral-strong');
    case 'NEW_LEAD':       return getToneClassName('info');
    case 'CONTACTED':      return 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:border-cyan-500/20';
    case 'DEMO_SCHEDULED': return getToneClassName('warning');
    case 'PROPOSAL_SENT':  return getToneClassName('primary');
    // Plan
    case 'DRAFT':     return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/20';
    case 'APPROVED':  return getToneClassName('success');
    case 'EXECUTING': return getToneClassName('info');
    case 'COMPLETED': return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20';
    case 'CANCELLED': return getToneClassName('neutral-strong');
    // IT Service
    case 'PENDING_APPROVAL': return getToneClassName('warning');
    case 'FULFILLING':       return getToneClassName('info');
    case 'FULFILLED':        return getToneClassName('success');
    case 'REJECTED':         return getToneClassName('danger');
    default:                 return 'bg-muted text-muted-foreground border-border';
  }
};

export const getTicketTypeClassName = (type) => {
  switch (type) {
    case 'FEATURE_REQUEST':
      return 'bg-primary/10 text-primary border-primary/20';
    case 'CHANGE_REQUEST':
      return 'bg-fuchsia-100 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-200 dark:border-fuchsia-500/20';
    case 'LEAD':
      return 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20';
    case 'PLAN':
      return 'bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/20';
    case 'IT_SERVICE':
      return 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/20';
    default: // ISSUE
      return 'bg-muted text-muted-foreground border-border';
  }
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatTicketDate = (value, options = {}) => {
  if (!value) return 'Not scheduled';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day:   'numeric',
    year:  'numeric',
    ...options,
  }).format(new Date(value));
};

export const getTicketActivityCopy = (entry) => {
  const prefix = entry.authorName ? `${entry.authorName}: ` : '';

  if (entry.text) return `${prefix}${entry.text}`;

  if (entry.type === 'ASSIGNMENT_CHANGE') {
    if (entry.toName) return `${prefix}Ticket assigned to ${entry.toName}`;
    return `${prefix}Ticket unassigned`;
  }

  if (entry.type === 'STATUS_CHANGE') {
    const from = TICKET_STATUS_LABELS[entry.from] ?? entry.from;
    const to   = TICKET_STATUS_LABELS[entry.to]   ?? entry.to;
    return `${prefix}Status changed from ${from} to ${to}`;
  }

  if (entry.type === 'PROMOTED')          return `${prefix}Ticket promoted to task`;
  if (entry.type === 'AUTO_TASK_CREATED') return `${prefix}Task auto-generated from workflow trigger`;

  return `${prefix}${(entry.type || 'ACTIVITY').replace(/_/g, ' ')}`;
};
